/**
 * ChangeLogBlock - Display report change history
 */

import React from 'react'
import { ChangeLogBlock as ChangeLogBlockType } from '../../../../lib/cockpit/schemas'

interface ChangeLogBlockProps {
  data: ChangeLogBlockType['data']
  blockId: string
  compact?: boolean
}

const TYPE_STYLES = {
  added: {
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-100 dark:bg-green-900/30',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    ),
    label: 'Added',
  },
  updated: {
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    label: 'Updated',
  },
  removed: {
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-100 dark:bg-red-900/30',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
      </svg>
    ),
    label: 'Removed',
  },
  corrected: {
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    label: 'Corrected',
  },
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

function ChangeLogBlock({ data, blockId, compact }: ChangeLogBlockProps) {
  // Sort by date descending
  const sortedEntries = [...data.entries].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  )

  if (compact) {
    const latestEntry = sortedEntries[0]
    if (!latestEntry) return <div className="text-sm text-gray-500">No changes recorded</div>
    
    const style = TYPE_STYLES[latestEntry.type as keyof typeof TYPE_STYLES]
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className={style.color}>{style.icon}</span>
        <span className="text-gray-500 dark:text-gray-400">{formatDate(latestEntry.date)}:</span>
        <span className="text-gray-700 dark:text-gray-300 truncate">{latestEntry.description}</span>
      </div>
    )
  }

  if (sortedEntries.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No changes recorded
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
      
      <div className="space-y-4">
        {sortedEntries.map(entry => {
          const style = TYPE_STYLES[entry.type as keyof typeof TYPE_STYLES]
          
          return (
            <div key={entry.id} className="relative pl-10">
              {/* Icon */}
              <div className={`
                absolute left-1.5 w-5 h-5 rounded-full flex items-center justify-center
                ${style.bg} ${style.color}
                ring-4 ring-white dark:ring-gray-900
              `}>
                {style.icon}
              </div>
              
              {/* Content */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className={`text-xs font-medium uppercase ${style.color}`}>
                    {style.label}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(entry.date)}
                  </span>
                </div>
                <p className="text-sm text-gray-800 dark:text-gray-200">
                  {entry.description}
                </p>
                {entry.affected_sections && entry.affected_sections.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {entry.affected_sections.map((section: string) => (
                      <span
                        key={section}
                        className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
                      >
                        {section}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ChangeLogBlock



